import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from './OrganizationContext';
import { getProfile } from '../services/profileService';
import { cacheService } from '../services/cacheService';
import { User } from '../types';

interface ProfileContextType {
  profile: User | null;
  loading: boolean;
  reloadProfile: () => Promise<void>;
}

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const { loading: orgLoading } = useOrganization();
  const [profile, setProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const loadingRef = useRef(false);

  const loadProfile = useCallback(async () => {
    // Aguardar organização estar carregada antes de carregar perfil
    if (orgLoading) {
      setLoading(true);
      return;
    }

    if (!user?.uid) {
      setProfile(null);
      setLoading(false);
      return;
    }

    // Evitar múltiplas chamadas simultâneas
    if (loadingRef.current) {
      return;
    }

    try {
      loadingRef.current = true;
      
      // Verificar cache primeiro
      const cachedProfile = cacheService.getCachedProfile(user.uid);
      if (cachedProfile) {
        setProfile(cachedProfile);
        setLoading(false);
        loadingRef.current = false;
        return;
      }

      // Se não tem cache, buscar da API
      setLoading(true);
      const userProfile = await getProfile();
      
      // Salvar no cache
      cacheService.setCachedProfile(user.uid, userProfile);
      
      setProfile(userProfile);
    } catch (error) {
      console.error('Error loading profile:', error);
      setProfile(null);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [user?.uid, orgLoading]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  const reloadProfile = useCallback(async () => {
    if (!user?.uid) return;
    
    // Limpar cache e recarregar
    cacheService.clearProfileCache(user.uid);
    await loadProfile();
  }, [user?.uid, loadProfile]);

  return (
    <ProfileContext.Provider value={{ profile, loading, reloadProfile }}>
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfile = (): ProfileContextType => {
  const context = useContext(ProfileContext);
  if (context === undefined) {
    throw new Error('useProfile must be used within a ProfileProvider');
  }
  return context;
};
